import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { validate } from '@adornis/validation/decorators.js';
import { email } from '@adornis/validation/functions/email.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { html } from 'lit';

@Entity()
export class PilotPartnerApplication extends MongoEntity {
  static override _class = 'PilotPartnerApplication';
  static override _collectionName = 'pilot-partner-applications';

  @Field(type => String)
  @validate(nonOptional())
  schoolName!: string;

  @Field(type => String)
  @validate(nonOptional())
  contactName!: string;

  @Field(type => String)
  @validate(email())
  schoolEmail!: string;

  @Field(type => String)
  subjectArea!: string;

  @Field(type => Boolean)
  @validate(nonOptional({ allowFalsy: false }))
  dataSubmissionAgreement!: boolean;

  @Field(type => String)
  role: Maybe<string>;
}

export const registerPilotPartnerApplication = registerMutation({
  type: () => Boolean,
  params: [
    {
      name: 'input',
      type: () => PilotPartnerApplication,
    },
  ],
  operationName: 'registerPilotPartnerApplication',
  resolve: (input: PilotPartnerApplication) => async () => {
    await input.create();

    // Send email notification
    const mailer = AdornisMailer.getInstance();
    await mailer.sendMail(
      await Message.compose({
        subject: 'New Pilot Partner Application Received',
        html: html`<p>A new pilot partner application has been submitted:</p>
          <ul>
            <li><strong>School Name:</strong> ${input.schoolName}</li>
            <li><strong>Contact Name:</strong> ${input.contactName}</li>
            <li><strong>School Email:</strong> ${input.schoolEmail}</li>
            <li><strong>Subject Area:</strong> ${input.subjectArea}</li>
            <li>
              <strong>Data Submission Agreement:</strong> ${input.dataSubmissionAgreement ? 'Accepted' : 'Not Accepted'}
            </li>
          </ul>
          <p>Please review the application in the system.</p>`,
      }),
      new Recipient('test@adornis.de'),
    );

    await mailer.sendMail(
      await Message.compose({
        subject: 'Pilot Partner Application Received',
        html: html`
          <p>Dear ${input.contactName},</p>
          <p>Thank you for applying to become a pilot partner!</p>
          <p>Our team will review your application and get back to you as soon as possible.</p>
          <p>We're looking forward to working with you!</p>
          <p>Best regards</p>
        `,
      }),
      new Recipient(input.schoolEmail),
    );

    return true;
  },
});
